import {_} from 'lodash';
import * as ko from "knockout";
import {AbstractHistory, template as tpl} from "./AbstractHistory";
import {nsRequest} from "../../../req/fn";
import {IHistory, IObjectData, IResponse, IRouteDefinitionParams, ITableColumn} from "../../../index";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {AVAILABLE_MAX_NUM_SEARCH, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../constants";
import {getSearchParamsFn} from "../../../util/request";
import {nsLanguage} from "../../../util/nsLanguage";
import {global, lm} from "../../../appContext";
import {isRoutePermissionOkay} from "../../../constants/RouteDefinitions";
import getPromiseRequest = nsRequest.getPromiseRequest;

export let template = tpl.replace(
    '<!-- TEMPLATE_INPUT_AFTER -->', `
<!-- ko if: showPickNumResults() --> 
<div class="input-group-addon" data-bind="tt: 'maximum number of hits in selection'">
    <fa-icon params="icon: 'fa-sigma'"></fa-icon>
</div>
<div class="input-group-btn num-results" data-bind="tt: 'maximum number of hits in selection'">
    <select class="btn num-results" data-bind="
        options: getAvailableNumSearch(),
        optionsText: num => getAvailableNumSearchText(num),
        value: $component.maxNumSearch,
    "></select>
</div>
<!-- /ko -->
`
)

export class viewModel extends AbstractHistory {

    protected readonly heading = 'view.objectdetail.history'
    private readonly routeId = 'object-history'
    private override readonly toLocaleString = nsLanguage.toLocaleString
    private readonly AVAILABLE_MAX_NUM_SEARCH = AVAILABLE_MAX_NUM_SEARCH
    private readonly maxNumSearch: KnockoutObservable<typeof AVAILABLE_MAX_NUM_SEARCH[number] | null> = ko.observable(null);

    private readonly req_getobject = getPromiseRequest('getobject');
    private readonly req_searchobjecthistory = getPromiseRequest('searchobjecthistory');

    protected override _original = undefined as unknown as IObjectData;
    protected res_searchobjecthistory!: IHistory[]

    protected TABLE = ko.pureComputed((): ITableColumn<IHistory>[] => {
        const ret: ITableColumn<IHistory>[] = [];
        ret.push({
            key: "id",
            sort: true,
        }, {
            key: "registered",
            sort: true,
        }, {
            label: 'type',
            key: "entrytype",
            sort: true,
        }, {
            label: "attribute",
            key: "attributename",
            sort: true,
        }, {
            key: "oldvalue",
            sort: true,
            css: 'wordBreak--all',
        }, {
            key: "newvalue",
            sort: true,
            css: 'wordBreak--all',
        }, {
            key: 'departmentid',
            sort: true,
            label: 'department',
            showContent: o => !!parseInt(o.departmentid ?? '0'),
        });

        if (this.toggleComment()) {
            ret.push({
                key: "comment",
                sort: true,
                binding: true,
            });
        }

        ret.push({
            label: "error message",
            key: "lasterror",
            sort: true,
            css: 'wordBreak--all text-danger',
        }, {
            label: "synchronization step",
            key: "syncstepid",
            getComponent: o => {
                return {
                    name: 'component/variable/syncstep-or-companysyncstep',
                    params: {id: o.syncstepid, linkable: isRoutePermissionOkay('syncstatusv2')},
                }
            },
            sort: true,
        });

        return ret;
    });

    constructor(params: IRouteDefinitionParams) {
        super(params);
        const initialMaxNumSearch = (() => {
            let indexMaxNumSearch = 0
            if (this.__objectTypeId == ObjectTypeId.Group) {
                indexMaxNumSearch = 1
            }
            return AVAILABLE_MAX_NUM_SEARCH[indexMaxNumSearch] ?? AVAILABLE_MAX_NUM_SEARCH[0]!;
        })();
        this.maxNumSearch(initialMaxNumSearch ?? null)
        this.kosub.subscribe(this.maxNumSearch, () => {
            const totalNumAlreadyLoaded = this.res_searchobjecthistory.length;
            if (totalNumAlreadyLoaded < (this.maxNumSearch() ?? 0) && totalNumAlreadyLoaded < this.totalResults) {
                this.init()
            } else {
                this.pagenr(0)
                this._data(this.res_searchobjecthistory.slice(0, this.maxNumSearch() ?? undefined))
            }
        })
        this.init();
    }

    protected override init(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        return Promise.resolve()
            .then(() => this.req_getobject
                .request({id: this.__id, manual: !!this.__routeDef.manual})
                .then((data: [IObjectData]) => {
                    this._original = data[0];
                })
            )
            .then(() => {
                if (!global.permissionValidator.isMinDepartmentAdmin()) {
                    this.totalResults = this._original.history.length
                    this.res_searchobjecthistory = this._original.history
                    return Promise.resolve()
                }
                const maxNumSearch = this.maxNumSearch();
                const params = getSearchParamsFn('searchobjecthistory')({
                    ...(maxNumSearch != null ? {resultsprpage: maxNumSearch} : {}),
                    asc: false,
                })
                return this.req_searchobjecthistory
                    .request(params, true)
                    .then((resp: IResponse<IHistory[]>) => {
                        this.totalResults = resp.totalresults
                        this.res_searchobjecthistory = resp.data
                    })
            })
            .then(() => super.init());
    }

    protected _getObjectHistory(): IHistory[] {
        return this.res_searchobjecthistory
    }

    private getAvailableNumSearch() {
        const ret = this.AVAILABLE_MAX_NUM_SEARCH.filter(num => num < this.totalResults)

        // always include one item "higher" if available
        if (ret.length < this.AVAILABLE_MAX_NUM_SEARCH.length) {
            const next = this.AVAILABLE_MAX_NUM_SEARCH[ret.length];
            if (next !== undefined) {
                ret.push(next);
            }
        }

        return ret
    }

    private getAvailableNumSearchText(num: number) {
        return (_.last(this.getAvailableNumSearch()) == num && this.totalResults <= MAX_RESULTS_PER_PAGE)
            ? `${this.totalResults} (${lm.t('All|ac')})`
            : num
    }

    private showPickNumResults() {
        return this.totalResults > this.AVAILABLE_MAX_NUM_SEARCH[0]
    }

    protected override isRequesting(): boolean {
        const a = this.req_getobject?.isRequesting()
        const b = this.req_searchobjecthistory?.isRequesting()
        return a || b || super.isRequesting();
    }

    override dispose() {
        super.dispose();
        this.req_getobject.abort()
        this.req_searchobjecthistory.abort()
    }

}
